import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "@/components/ErrorBoundary";
import CookieConsent from "@/components/CookieConsent";
import { lazy, Suspense, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const Index = lazy(() => import("./pages/Index"));

const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const PlayGame = lazy(() => import("./pages/PlayGame"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const InfoForParents = lazy(() => import("./pages/InfoForParents"));
const AllGames = lazy(() => import("./pages/AllGames"));
const Profile = lazy(() => import("./pages/Profile"));
const RamayanEpic = lazy(() => import("./pages/RamayanEpic"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Help = lazy(() => import("./pages/Help"));
const EmbedGame = lazy(() => import("./pages/EmbedGame"));

const queryClient = new QueryClient();

// Detect if current host is the dedicated admin subdomain
const isAdminSubdomain = (() => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  return host === 'prabandhak.dhurandhargames.com' || host.startsWith('prabandhak.');
})();

const AuthCallbackHandler = ({ children }: { children: React.ReactNode }) => {
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.location.hash.includes('access_token=') && window.location.hash.includes('refresh_token=');
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !isProcessingOAuth) return;

    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');

    if (!accessToken || !refreshToken) {
      setIsProcessingOAuth(false);
      return;
    }

    const restoreOAuthSession = async () => {
      try {
        await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        window.history.replaceState({}, document.title, isAdminSubdomain ? '/prabandhak' : '/');
      } catch (error) {
        console.error('Failed to restore OAuth session:', error);
        window.history.replaceState({}, document.title, '/auth');
      } finally {
        setIsProcessingOAuth(false);
      }
    };

    void restoreOAuthSession();
  }, [isProcessingOAuth]);

  if (isProcessingOAuth) {
    return <div className="min-h-screen bg-background" />;
  }

  return <>{children}</>;
};

// Routes for the dedicated admin subdomain (prabandhak.dhurandhargames.com)
// Only the management panel + auth flows are exposed; everything else redirects to /prabandhak
const AdminOnlyRoutes = () => (
  <Routes>
    <Route path="/" element={<Navigate to="/prabandhak" replace />} />
    <Route path="/auth" element={<Auth />} />
    <Route path="/prabandhak" element={<Admin />} />
    {/* Backwards-compat: old /admin path still works on the subdomain */}
    <Route path="/admin" element={<Navigate to="/prabandhak" replace />} />
    <Route path="*" element={<Navigate to="/prabandhak" replace />} />
  </Routes>
);

// Routes for the main public domain (dhurandhargames.com)
// /prabandhak and /admin are blocked here — users must use the subdomain instead
const PublicRoutes = () => (
  <Routes>
    <Route path="/" element={<Index />} />
    <Route path="/about" element={<About />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms-of-service" element={<TermsOfService />} />
    <Route path="/info-for-parents" element={<InfoForParents />} />
    <Route path="/all-games" element={<AllGames />} />
    <Route path="/play/:gameId" element={<PlayGame />} />
    <Route path="/profile" element={<Profile />} />
    <Route path="/ramayan-epic" element={<RamayanEpic />} />
    <Route path="/blog" element={<Blog />} />
    <Route path="/blog/:slug" element={<BlogPost />} />
    <Route path="/help" element={<Help />} />
    {/* Isolated iframe embed route for heavy 3D games — no header/footer/wrapper */}
    <Route path="/embed/:gameId" element={<EmbedGame />} />
    <Route path="/auth" element={<Auth />} />
    {/* Management panel moved to dedicated subdomain. Hide both routes on the main domain. */}
    <Route path="/prabandhak" element={<Navigate to="/" replace />} />
    <Route path="/admin" element={<Navigate to="/" replace />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const App = () => (
  <ErrorBoundary>
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthCallbackHandler>
          {!isAdminSubdomain && <CookieConsent />}
          <AuthProvider>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              {isAdminSubdomain ? <AdminOnlyRoutes /> : <PublicRoutes />}
            </Suspense>
          </AuthProvider>
        </AuthCallbackHandler>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
  </ErrorBoundary>
);

export default App;
